/**
 * A canned response, laid out the way an agent types.
 *
 * Snippets are stored as paragraphs -- `<p>…</p><ul>…</ul><p>…</p>` -- which
 * is the right shape for storing and for the preview. The reply box is not
 * built from paragraphs, though: Enter makes a plain line, and agents separate
 * one thought from the next with an empty line, Enter twice. Dropped in as
 * they were, a snippet's paragraphs sat half a line apart in a reply whose own
 * paragraphs are a whole line apart, and read as squashed beside the
 * greeting typed above them -- "more condensed than the standard helpdesk
 * formatting", as the desk put it.
 *
 * So each top-level paragraph becomes a line, with an empty line between it
 * and the next, exactly as if it had been typed -- and so does a run of bare
 * text and inline markup between them. Lists and everything inside
 * a block are left alone. Only the top level is touched: a paragraph somebody
 * nested inside a list item is the snippet's own business.
 */
export function asTyped(html: string): string {
  const host = document.createElement("div");
  host.innerHTML = html;

  /* Already lines, or bare text: nothing to translate. */
  if (![...host.childNodes].some(isParagraph)) return html;

  /*
   * Top-level nodes, grouped into the lines they will become. A block is its
   * own; a run of inline nodes between blocks -- "Hi <b>[[name]]</b>," before
   * the first paragraph -- is one line, because that is how it rendered. Taken
   * node by node, the greeting came out as three lines with gaps between.
   */
  const blocks: Node[][] = [];
  let run: Node[] = [];
  const endRun = () => {
    if (run.some((node) => !isBlank(node))) blocks.push(run);
    run = [];
  };
  for (const node of [...host.childNodes]) {
    if (isBlock(node)) {
      endRun();
      blocks.push([node]);
    } else {
      run.push(node);
    }
  }
  endRun();

  const out = document.createElement("div");
  blocks.forEach((group, index) => {
    const [first] = group;
    if (group.length === 1 && isParagraph(first)) {
      /* The attributes come along: a centred sign-off is still centred. */
      const line = document.createElement("div");
      for (const attribute of [...first.attributes]) {
        line.setAttribute(attribute.name, attribute.value);
      }
      line.append(...first.childNodes);
      if (!line.hasChildNodes()) line.append(document.createElement("br"));
      out.append(line);
    } else if (group.length === 1 && isBlock(first)) {
      out.append(first);
    } else {
      const line = document.createElement("div");
      line.append(...group);
      out.append(line);
    }

    /* A blank line between two lines of prose, as Enter twice would make.
       Not beside a list, which carries its own space above and below -- a
       blank line there as well left a hole either side of it. Nor after the
       last block: the caret lands at the end of the snippet, not on an empty
       line under it. */
    const next = blocks[index + 1];
    if (next && !isList(group) && !isList(next)) {
      const gap = document.createElement("div");
      gap.append(document.createElement("br"));
      out.append(gap);
    }
  });

  return out.innerHTML;
}

/* What the reply box lays out as a line of its own. Anything else at the top
   level is inline and joins the line beside it. */
const BLOCK_TAGS = new Set([
  "P", "DIV", "UL", "OL", "BLOCKQUOTE", "PRE", "TABLE", "HR",
  "H1", "H2", "H3", "H4", "H5", "H6",
]);

function isBlock(node: Node | undefined): node is HTMLElement {
  return node instanceof HTMLElement && BLOCK_TAGS.has(node.tagName);
}

function isParagraph(node: Node | undefined): node is HTMLElement {
  return node instanceof HTMLElement && node.tagName === "P";
}

function isBlank(node: Node): boolean {
  return node.nodeType === Node.TEXT_NODE && !node.textContent?.trim();
}

function isList(group: Node[]): boolean {
  const [node] = group;
  return (
    group.length === 1 &&
    node instanceof HTMLElement &&
    (node.tagName === "UL" || node.tagName === "OL")
  );
}
